import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, AreaChart, Area,
} from "recharts";
import {
    Thermometer, Droplets, Wind as WindIcon, CloudRain, Compass, Gauge,
    Sun, Snowflake, Flame, ArrowLeft,
} from "lucide-react";
import { fetchStation, fetchObservations, fetchThresholds } from "@/lib/api";
import {
    fdiLevel, fdiPosition, deltaTLevel, deltaTPosition, frostLevel, frostPosition,
    sprayBadge, fmt, LEVEL_LABELS, RISK_COLORS,
} from "@/lib/risk";
import { RiskBar } from "@/components/RiskBar";
import StatusBadge from "@/components/StatusBadge";
import HfdiTrendCard from "@/components/HfdiTrendCard";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const RANGES = [
    { key: "today", label: "Today" },
    { key: "7d", label: "7 Days" },
    { key: "30d", label: "30 Days" },
    { key: "growing", label: "Growing" },
    { key: "12m", label: "12 Months" },
];

export default function StationDetailPage() {
    const { id } = useParams();
    const [range, setRange] = useState("7d");

    const { data: s } = useQuery({ queryKey: ["station", id], queryFn: () => fetchStation(id), refetchInterval: 60_000 });
    const { data: obs, isLoading: obsLoading } = useQuery({ queryKey: ["obs", id, range], queryFn: () => fetchObservations(id, { range }) });
    const { data: thr } = useQuery({ queryKey: ["thresholds"], queryFn: fetchThresholds });

    if (!s || !thr) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    const cur = s.current;
    const fdiL = fdiLevel(cur.fdi, thr.fdi);
    const dtL = deltaTLevel(cur.delta_t, thr.delta_t);
    const frostL = frostLevel(s.forecast_min, thr.frost);
    const spray = sprayBadge(s.spray);

    const data = obs?.data || [];
    const isHourly = obs?.granularity === "hourly";
    const xKey = isHourly ? "ts" : "date";
    const fmtX = (v) => {
        const d = new Date(v);
        return isHourly
            ? d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })
            : d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
    };

    return (
        <div className="space-y-6" data-testid="station-detail-page">
            <Link to="/stations" data-testid="back-to-stations" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900">
                <ArrowLeft className="h-4 w-4" /> All stations
            </Link>

            {/* Header */}
            <div className="fw-card p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Station</div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{s.name}</h1>
                        <div className="text-xs text-slate-500 mt-1">Last updated {new Date(s.updated_at).toLocaleString("en-AU")}</div>
                    </div>
                    <div className="flex items-end gap-2">
                        <div className="fw-metric text-5xl font-extrabold text-slate-900">{fmt(cur.temp, 1)}°C</div>
                        <div className="pb-1.5">
                            <div className="text-sm font-semibold text-slate-700">{s.condition}</div>
                            <div className="text-xs text-slate-500">Feels the same</div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-5">
                    <HeaderStat icon={Droplets} label="Humidity" value={`${fmt(cur.humidity, 0)}%`} />
                    <HeaderStat icon={WindIcon} label="Wind" value={`${fmt(cur.wind, 1)} km/h`} />
                    <HeaderStat icon={Compass} label="Direction" value={cur.wind_dir} />
                    <HeaderStat icon={WindIcon} label="Gust" value={`${fmt(cur.gust, 1)} km/h`} />
                    <HeaderStat icon={CloudRain} label="Rain today" value={`${fmt(s.rain_24h, 1)} mm`} />
                    <HeaderStat icon={Gauge} label="Pressure" value={`${fmt(cur.pressure, 1)} hPa`} />
                </div>
            </div>

            {/* Risk cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <RiskCard icon={Flame} title="HFDI" value={fmt(cur.fdi, 0)} unit="" level={fdiL}
                    sub={LEVEL_LABELS[fdiL]} pos={fdiPosition(cur.fdi, thr.fdi)} />
                <RiskCard icon={Droplets} title="Delta T" value={fmt(cur.delta_t, 1)} unit="°C" level={dtL}
                    sub={dtL === "green" ? "Good" : LEVEL_LABELS[dtL]} pos={deltaTPosition(cur.delta_t, thr.delta_t)} />
                <RiskCard icon={Snowflake} title="Frost" value={fmt(s.forecast_min, 1)} unit="°C" level={frostL}
                    sub={`${LEVEL_LABELS[frostL]} risk`} pos={frostPosition(s.forecast_min, thr.frost)} />
                <div className="fw-card p-5">
                    <div className="flex items-start justify-between">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Spray</div>
                        <WindIcon className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="mt-2 fw-metric text-2xl font-extrabold text-slate-900">{spray.label}</div>
                    <div className="mt-1"><StatusBadge level={spray.level} label={spray.label} /></div>
                    <div className="text-xs text-slate-500 mt-3">
                        Wind {fmt(cur.wind, 1)}km/h · Gust {fmt(cur.gust, 1)}km/h · Delta T {fmt(cur.delta_t, 1)}°C
                    </div>
                </div>
                <div className="fw-card p-5">
                    <div className="flex items-start justify-between">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Rainfall</div>
                        <CloudRain className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="mt-2 fw-metric text-2xl font-extrabold text-slate-900">{fmt(s.rain_24h, 1)} mm</div>
                    <div className="grid grid-cols-2 gap-1 text-xs text-slate-600 mt-2">
                        <div>7d <span className="fw-metric font-semibold text-slate-900">{fmt(s.rain_7d, 1)}</span></div>
                        <div>30d <span className="fw-metric font-semibold text-slate-900">{fmt(s.rain_30d, 1)}</span></div>
                        <div>YTD <span className="fw-metric font-semibold text-slate-900">{fmt(s.rain_ytd, 0)}</span></div>
                        <div>Season <span className="fw-metric font-semibold text-slate-900">{fmt(s.rain_growing_season, 0)}</span></div>
                    </div>
                </div>
            </div>

            {/* HFDI over time — banded chart with cease-harvest threshold */}
            <HfdiTrendCard station={id} compact />

            {/* Range selector */}
            <Tabs value={range} onValueChange={setRange}>
                <TabsList className="bg-white border border-slate-200 p-1">
                    {RANGES.map((r) => (
                        <TabsTrigger key={r.key} value={r.key} data-testid={`range-${r.key}`}>{r.label}</TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            {/* Charts grid — only mount recharts once obs data is present so
                the ResponsiveContainer measures the grid track correctly. */}
            {obsLoading || !obs ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="fw-card p-5 h-64 animate-pulse bg-slate-50" data-testid="chart-skeleton" />
                    ))}
                </div>
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" key={`charts-${range}-${data.length}`}>
                <ChartCard title="Rainfall" unit="mm">
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={fmtX} contentStyle={tipStyle} />
                        <Bar dataKey="rain" fill="#2563EB" radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ChartCard>
                <ChartCard title="Temperature" unit="°C">
                    <AreaChart data={data}>
                        <defs>
                            <linearGradient id="tempFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#EA580C" stopOpacity={0.35} />
                                <stop offset="100%" stopColor="#EA580C" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={fmtX} contentStyle={tipStyle} />
                        <Area type="monotone" dataKey={isHourly ? "temp" : "avg_temp"} stroke="#EA580C" fill="url(#tempFill)" strokeWidth={2} />
                        {!isHourly && <Line type="monotone" dataKey="max_temp" stroke="#DC2626" strokeWidth={1.5} dot={false} />}
                        {!isHourly && <Line type="monotone" dataKey="min_temp" stroke="#2563EB" strokeWidth={1.5} dot={false} />}
                    </AreaChart>
                </ChartCard>
                <ChartCard title="Relative Humidity" unit="%">
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={fmtX} contentStyle={tipStyle} />
                        <Line type="monotone" dataKey={isHourly ? "humidity" : "avg_humidity"} stroke="#0EA5E9" strokeWidth={2} dot={false} />
                    </LineChart>
                </ChartCard>
                <ChartCard title="Delta T" unit="°C">
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={fmtX} contentStyle={tipStyle} />
                        <Line type="monotone" dataKey={isHourly ? "delta_t" : "avg_delta_t"} stroke="#16A34A" strokeWidth={2} dot={false} />
                    </LineChart>
                </ChartCard>
                <ChartCard title="Wind Speed" unit="km/h">
                    <AreaChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={fmtX} contentStyle={tipStyle} />
                        <Area type="monotone" dataKey={isHourly ? "wind" : "avg_wind"} stroke="#0F172A" fill="#94A3B8" fillOpacity={0.35} strokeWidth={2} />
                        {isHourly && <Line type="monotone" dataKey="gust" stroke="#DC2626" strokeWidth={1.5} dot={false} />}
                    </AreaChart>
                </ChartCard>
                <ChartCard title="Dew Point" unit="°C">
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={fmtX} contentStyle={tipStyle} />
                        <Line type="monotone" dataKey={isHourly ? "dew_point" : "avg_dew_point"} stroke="#7C3AED" strokeWidth={2} dot={false} />
                    </LineChart>
                </ChartCard>
                <ChartCard title="Solar Radiation" unit="W/m²">
                    <AreaChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={fmtX} contentStyle={tipStyle} />
                        <Area type="monotone" dataKey={isHourly ? "solar" : "avg_solar"} stroke="#CA8A04" fill="#FDE68A" fillOpacity={0.6} strokeWidth={2} />
                    </AreaChart>
                </ChartCard>
                <ChartCard title="Atmospheric Pressure" unit="hPa">
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 11 }} />
                        <YAxis domain={["dataMin - 2", "dataMax + 2"]} tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={fmtX} contentStyle={tipStyle} />
                        <Line type="monotone" dataKey={isHourly ? "pressure" : "avg_pressure"} stroke="#0F766E" strokeWidth={2} dot={false} />
                    </LineChart>
                </ChartCard>
                {!isHourly && (
                    <ChartCard title="Evapotranspiration (ET)" unit="mm">
                        <BarChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey={xKey} tickFormatter={fmtX} tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip labelFormatter={fmtX} contentStyle={tipStyle} />
                            <Bar dataKey="sum_et" fill="#0EA5E9" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ChartCard>
                )}
            </div>
            )}
        </div>
    );
}

const tipStyle = {
    background: "white", border: "1px solid #E2E8F0", borderRadius: 12, fontSize: 12,
};

function HeaderStat({ icon: Icon, label, value }) {
    return (
        <div className="bg-slate-50 rounded-xl border border-slate-100 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <Icon className="h-3 w-3" /> {label}
            </div>
            <div className="fw-metric font-bold text-slate-900 text-lg mt-1">{value}</div>
        </div>
    );
}

function RiskCard({ icon: Icon, title, value, unit, level, sub, pos }) {
    return (
        <div className="fw-card p-5" data-testid={`risk-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-start justify-between">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{title}</div>
                <Icon className="h-4 w-4 text-slate-400" />
            </div>
            <div className="mt-2 fw-metric text-2xl font-extrabold text-slate-900">
                {value}<span className="text-base text-slate-400 font-semibold">{unit}</span>
            </div>
            <div className="text-xs font-semibold mt-0.5" style={{ color: RISK_COLORS[level].text }}>{sub}</div>
            <div className="mt-3"><RiskBar level={level} position={pos} label={title} /></div>
        </div>
    );
}

function ChartCard({ title, unit, children }) {
    return (
        <div className="fw-card p-4 sm:p-5" data-testid={`chart-${title.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: "Outfit" }}>{title}</h3>
                <span className="text-[11px] text-slate-500 fw-metric">{unit}</span>
            </div>
            {/* Fixed pixel height on ResponsiveContainer avoids the recharts
                "width(-1)/height(-1)" bug when the chart cards mount inside a
                CSS grid track that hasn't laid out yet. */}
            <ResponsiveContainer width="100%" height={220} debounce={50}>
                {children}
            </ResponsiveContainer>
        </div>
    );
}
